import { useCallback, useEffect, useRef, useState } from "react"
import {
  useDismissNotificationMutation,
  useSettlementNotification,
} from "@/notification"
import type { Notification } from "@/notification"

// How long to wait for the realtime settlement event before flagging it slow.
// Card settlement is near-instant; this only guards a dropped event or a
// stalled worker. It does NOT re-enable the paid checkout (see below).
const SETTLEMENT_TIMEOUT_MS = 30_000

type Handoff = {
  /** A checkout has been paid and we're awaiting its outcome — stay locked. */
  readonly submitted: boolean
  readonly timedOut: boolean
  readonly awaitSettlement: (paymentIntentId: string) => void
}

/**
 * Bridges the gap between triggering finalize and the payment outcome arriving
 * over realtime. After finalize the dialog calls awaitSettlement(pi) and shows
 * a non-repeatable processing state; when the matching settlement notification
 * lands it dismisses the bell item, hands the notification to onComplete (which
 * closes the dialog), and clears the lock back to a fresh form.
 *
 * The lock is cleared ONLY by consuming the settlement — never on timeout, and
 * (deliberately) never on dialog close. The payment already succeeded, so
 * re-enabling pay would let the same checkout charge again; closing and
 * reopening keeps showing the processing state until the event lands (or the
 * page is reloaded, after which the bell item recovers it). A genuinely dropped
 * event leaves a stuck spinner — the safe failure, strictly better than a
 * double charge.
 */
export default function useSettlementHandoff(
  onComplete: (settlement: Notification) => void,
): Handoff {
  const [pendingPaymentIntentId, setPendingPaymentIntentId] = useState<
    string | null
  >(null)
  const [timedOut, setTimedOut] = useState(false)
  const { settlement } = useSettlementNotification(pendingPaymentIntentId)
  const [dismissNotification] = useDismissNotificationMutation()
  const consumedRef = useRef<string | null>(null)

  useEffect(() => {
    if (
      !pendingPaymentIntentId ||
      !settlement ||
      consumedRef.current === pendingPaymentIntentId
    ) {
      return
    }

    consumedRef.current = pendingPaymentIntentId
    void dismissNotification(settlement.id)
    onComplete(settlement)
    // Consuming the realtime event clears the in-flight lock back to a fresh
    // form. Deliberate setState from an effect — the settlement arrives through
    // the notifications cache, so there is no event callback to host it; the ref
    // above keeps it firing exactly once.
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
    setPendingPaymentIntentId(null)
  }, [pendingPaymentIntentId, settlement, dismissNotification, onComplete])

  useEffect(() => {
    if (!pendingPaymentIntentId) {
      return
    }

    const timer = setTimeout(() => {
      setTimedOut(true)
    }, SETTLEMENT_TIMEOUT_MS)

    return () => {
      clearTimeout(timer)
    }
  }, [pendingPaymentIntentId])

  const awaitSettlement = useCallback((paymentIntentId: string) => {
    setTimedOut(false)
    setPendingPaymentIntentId(paymentIntentId)
  }, [])

  return { submitted: !!pendingPaymentIntentId, timedOut, awaitSettlement }
}
